import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, gql } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { GET_JOB_CHECKLIST_COMPLETIONS } from '../../graphql/queries';
import { SAVE_JOB_CHECKLIST_ITEM } from '../../graphql/mutations';
import {
  applyCompletionDataToItems,
  buildCategoriesMap,
  countRequiredComplete,
  groupChecklistItemsByCategory,
} from './checklist-utils';

const GET_CHECKLIST_CATEGORIES = gql`
  query GetChecklistCategoriesForJobSection {
    checklistCategories {
      id
      title
      sortOrder
    }
  }
`;

const ChecklistIcon = () => (
  <svg className="h-6 w-6 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

export default function JobChecklistSection({ job, viewOnly = false, onChecklistUpdate }) {
  const [checklistItems, setChecklistItems] = useState([]);
  const [savingItemIds, setSavingItemIds] = useState({});

  const useChecklistStyle = job?.serviceType?.useChecklistStyle ?? false;
  const checklist = job?.serviceType?.checklist;
  const canSubmitChecklist = job?.serviceType?.canSubmitChecklist ?? false;
  const isReadOnly = viewOnly || !canSubmitChecklist;

  const { data: categoriesData, loading: categoriesLoading, error: categoriesError } = useQuery(
    GET_CHECKLIST_CATEGORIES,
    { skip: !useChecklistStyle || !checklist?.items?.length, fetchPolicy: 'network-only' }
  );

  const { data: completionsData, refetch: refetchCompletions } = useQuery(GET_JOB_CHECKLIST_COMPLETIONS, {
    variables: { jobId: job?.id },
    skip: !job?.id || !useChecklistStyle || !checklist?.id,
    fetchPolicy: 'network-only',
  });

  const [saveJobChecklistItem] = useMutation(SAVE_JOB_CHECKLIST_ITEM);

  const jobId = job?.id;
  const checklistId = checklist?.id;
  const checklistItemIds = checklist?.items?.map((item) => item.id).join(',') ?? '';

  const buildGroupedItemsFromSource = (completionQueryData) => {
    if (!checklist?.items?.length) return [];

    const categoriesMap = buildCategoriesMap(categoriesData, checklist.items, categoriesError);
    let groupedItems = groupChecklistItemsByCategory(
      checklist.items,
      categoriesMap,
      categoriesError,
      true
    );

    return applyCompletionDataToItems(
      groupedItems,
      completionQueryData,
      checklist.id,
      true
    );
  };

  useEffect(() => {
    if (!useChecklistStyle || !checklist?.items?.length) {
      setChecklistItems([]);
      return;
    }

    if (!categoriesData?.checklistCategories && categoriesLoading) {
      return;
    }

    setChecklistItems(buildGroupedItemsFromSource(completionsData));
  }, [
    jobId,
    checklistId,
    checklistItemIds,
    categoriesData,
    categoriesLoading,
    categoriesError,
    completionsData,
    useChecklistStyle,
  ]);

  if (!useChecklistStyle || !checklist?.items?.length) {
    return null;
  }

  const checklistTitle = checklist.title || 'Checklist';
  const requiredProgress = countRequiredComplete(checklistItems, true);
  const progressPercent = requiredProgress.total > 0
    ? Math.round((requiredProgress.completed / requiredProgress.total) * 100)
    : 0;
  const isFullyComplete = requiredProgress.total > 0 && requiredProgress.completed === requiredProgress.total;
  const showCategoryHeaders = checklistItems.length > 1;

  const handleChecklistItemToggle = async (categoryId, itemId, newChecked) => {
    if (isReadOnly) return;

    const previousItems = checklistItems;
    setChecklistItems(
      checklistItems.map(category => {
        if (category.id !== categoryId) return category;
        return {
          ...category,
          items: category.items.map(item =>
            item.id === itemId ? { ...item, checked: newChecked } : item
          )
        };
      })
    );

    setSavingItemIds(prev => ({ ...prev, [itemId]: true }));

    try {
      const response = await saveJobChecklistItem({
        variables: {
          input: {
            jobId: job.id,
            checklistId: checklist.id,
            itemId,
            completed: newChecked,
          }
        },
      });

      if (response.errors?.length) {
        throw new Error(response.errors[0].message);
      }

      const refetchResult = await refetchCompletions();
      const syncedItems = buildGroupedItemsFromSource(refetchResult.data);
      if (syncedItems.length > 0) {
        setChecklistItems(syncedItems);
      }

      onChecklistUpdate?.();

      const updatedItems = previousItems.map(category => {
        if (category.id !== categoryId) return category;
        return {
          ...category,
          items: category.items.map(item =>
            item.id === itemId ? { ...item, checked: newChecked } : item
          )
        };
      });

      const { completed, total } = countRequiredComplete(updatedItems, true);
      if (total > 0 && completed === total) {
        toast.success(`${checklistTitle} checklist completed!`);
      }
    } catch (error) {
      setChecklistItems(previousItems);
      toast.error(`Error saving checklist item: ${error.message}`);
    } finally {
      setSavingItemIds(prev => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    }
  };

  const handleItemRowClick = (categoryId, item, event) => {
    if (isReadOnly || savingItemIds[item.id]) return;
    if (event.target.type === 'checkbox') return;
    handleChecklistItemToggle(categoryId, item.id, !item.checked);
  };

  const handleItemRowKeyDown = (categoryId, item, event) => {
    if (isReadOnly || savingItemIds[item.id]) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleChecklistItemToggle(categoryId, item.id, !item.checked);
  };

  let itemIndex = 0;

  return (
    <div className="bg-white shadow rounded-lg mb-3 lg:mb-6 overflow-hidden border border-sky-100">
      <div className="bg-gradient-to-r from-sky-50 to-white px-4 py-4 sm:px-6 sm:py-5 border-b border-sky-100">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 rounded-lg bg-sky-100 p-2.5">
              <ChecklistIcon />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {checklistTitle} Checklist
              </h3>
              <p className="mt-0.5 text-sm text-gray-500">
                {isReadOnly
                  ? 'Review checklist progress for this job'
                  : 'Check off items as you complete them — changes save automatically'}
              </p>
            </div>
          </div>

          {requiredProgress.total > 0 && (
            <div className="sm:min-w-[180px] sm:text-right">
              <p className="text-sm font-medium text-gray-700">
                {requiredProgress.completed} of {requiredProgress.total} complete
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{progressPercent}% done</p>
            </div>
          )}
        </div>

        {requiredProgress.total > 0 && (
          <div className="mt-4 h-2 w-full rounded-full bg-sky-100 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ease-out ${
                isFullyComplete ? 'bg-green-500' : 'bg-sky-500'
              }`}
              style={{ width: `${progressPercent}%` }}
              role="progressbar"
              aria-valuenow={progressPercent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Checklist completion progress"
            />
          </div>
        )}
      </div>

      <div className="px-4 py-5 sm:p-6">
        {!canSubmitChecklist && !viewOnly && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-800">
            <svg className="h-5 w-5 flex-shrink-0 text-blue-500 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            This checklist is for reference only and cannot be edited.
          </div>
        )}

        {isFullyComplete && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-sm text-green-800">
            <svg className="h-5 w-5 flex-shrink-0 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            All required items are complete.
          </div>
        )}

        {categoriesLoading && checklistItems.length === 0 ? (
          <div className="flex items-center justify-center h-24">
            <svg className="animate-spin h-6 w-6 text-sky-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : (
          <div className="space-y-5">
            {checklistItems.map(category => (
              <div key={category.id}>
                {showCategoryHeaders && (
                  <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                    <span className="h-px flex-1 bg-gray-200" />
                    <span className="px-2">{category.title}</span>
                    <span className="h-px flex-1 bg-gray-200" />
                  </h4>
                )}
                <ul className="space-y-2">
                  {category.items.map(item => {
                    itemIndex += 1;
                    const isSaving = Boolean(savingItemIds[item.id]);
                    const isChecked = Boolean(item.checked);

                    return (
                      <li key={item.id}>
                        <div
                          role={isReadOnly ? undefined : 'button'}
                          tabIndex={isReadOnly ? undefined : 0}
                          onClick={(event) => handleItemRowClick(category.id, item, event)}
                          onKeyDown={(event) => handleItemRowKeyDown(category.id, item, event)}
                          className={`group flex items-start gap-3 rounded-lg border px-3 py-3 transition-all duration-150 sm:px-4 ${
                            isChecked
                              ? 'border-green-200 bg-green-50/80'
                              : 'border-gray-200 bg-gray-50/50 hover:border-sky-200 hover:bg-sky-50/50'
                          } ${!isReadOnly && !isSaving ? 'cursor-pointer' : ''} ${isSaving ? 'opacity-70' : ''}`}
                          aria-label={isReadOnly ? undefined : `Toggle ${item.title}`}
                        >
                          <span className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                            isChecked ? 'bg-green-500 text-white' : 'bg-white text-gray-500 ring-1 ring-gray-200'
                          }`}>
                            {isChecked ? (
                              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              itemIndex
                            )}
                          </span>

                          <div className="flex-shrink-0 pt-0.5" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleChecklistItemToggle(category.id, item.id, e.target.checked);
                              }}
                              disabled={isReadOnly || isSaving}
                              className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500 disabled:opacity-50"
                              aria-label={`Mark ${item.title} as complete`}
                            />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <span className={`text-sm font-medium leading-snug ${
                                isChecked ? 'text-gray-500 line-through decoration-gray-400' : 'text-gray-900'
                              }`}>
                                {item.title}
                                {item.isRequired && (
                                  <span className="ml-1 text-red-500" title="Required">*</span>
                                )}
                              </span>
                              {isSaving && (
                                <span className="flex-shrink-0 text-xs text-sky-600">Saving...</span>
                              )}
                              {!isSaving && isChecked && (
                                <span className="flex-shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                  Done
                                </span>
                              )}
                            </div>
                            {item.description && (
                              <p className={`mt-1 text-sm ${isChecked ? 'text-gray-400' : 'text-gray-500'}`}>
                                {item.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
